import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useAdminAuth } from "@/lib/hooks/useAdminAuth";
import { useAdminToggleMutation } from "@/lib/hooks/useAdminToggleMutation";
import {
  approveAppointmentsEnabledRequest,
  rejectAppointmentsEnabledRequest,
  setAppointmentsEnabled,
} from "@/lib/api/admin";
import type { Store } from "@/lib/api/types";
import { Button } from "@/components/ui/button";
import { formatDateTime } from "@/lib/utils/date";
import { CalendarClock } from "lucide-react";
import { AdminOverrideCard } from "@/components/AdminOverrideCard";
import { ConfirmDialog } from "@/components/ConfirmDialog";

type StoreAppointmentsEnabledSectionProps = {
  store: Store;
};

export function StoreAppointmentsEnabledSection({ store }: StoreAppointmentsEnabledSectionProps) {
  const qc = useQueryClient();
  const user = useAdminAuth();
  const [confirmOpen, setConfirmOpen] = useState(false);

  function invalidate() {
    void qc.invalidateQueries({ queryKey: ["store", store.id] });
  }

  const approveMutation = useMutation({
    mutationFn: async () => approveAppointmentsEnabledRequest(user!, store.id),
    onSuccess: () => {
      invalidate();
      toast.success("Appointment booking approved.");
    },
    onError: (err) => toast.error((err as Error).message),
  });

  const rejectMutation = useMutation({
    mutationFn: async () => rejectAppointmentsEnabledRequest(user!, store.id),
    onSuccess: () => {
      invalidate();
      toast.success("Appointment booking request rejected.");
    },
    onError: (err) => toast.error((err as Error).message),
  });

  const toggleMutation = useAdminToggleMutation<boolean>(
    store.id,
    setAppointmentsEnabled,
    "Appointment booking updated.",
    () => setConfirmOpen(false)
  );

  // Beauty & Personal Care stores get appointment booking automatically from their
  // category (mirrors nightclub → events in StoreEventsEnabledSection), so there is
  // no override to grant/revoke and no request to approve — hide the card entirely.
  if (store.category === "beauty") {
    return null;
  }

  const hasPendingRequest = store.appointments_enabled_requested_at && !store.appointments_enabled;

  return (
    <>
      <AdminOverrideCard
        icon={CalendarClock}
        title="Appointment Booking"
        statusLabel={store.appointments_enabled ? "Enabled" : "Disabled"}
        statusDescription={
          store.appointments_enabled
            ? "This store can configure staff/services and accept appointment bookings."
            : "This store cannot accept appointment bookings."
        }
        action={
          store.appointments_enabled
            ? {
                label: "Revoke",
                onClick: () => setConfirmOpen(true),
                disabled: toggleMutation.isPending,
              }
            : !hasPendingRequest
              ? {
                  label: "Grant",
                  onClick: () => setConfirmOpen(true),
                  disabled: toggleMutation.isPending,
                }
              : undefined
        }
      >
        {hasPendingRequest && (
          <div className="rounded-md border border-yellow-200 bg-yellow-50 p-3">
            <p className="text-xs font-medium text-yellow-800">Pending request</p>
            <p className="text-xs text-yellow-700">
              Requested {formatDateTime(store.appointments_enabled_requested_at!)}
            </p>
            <div className="mt-2 flex gap-2">
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                onClick={() => approveMutation.mutate()}
                disabled={approveMutation.isPending || rejectMutation.isPending}
              >
                Approve
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs text-destructive"
                onClick={() => rejectMutation.mutate()}
                disabled={approveMutation.isPending || rejectMutation.isPending}
              >
                Reject
              </Button>
            </div>
          </div>
        )}
      </AdminOverrideCard>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={
          store.appointments_enabled ? "Revoke appointment booking" : "Grant appointment booking"
        }
        description={
          store.appointments_enabled
            ? `Revoke appointment booking for "${store.name}"? Its appointment staff/services and existing bookings will be hidden until re-granted.`
            : `Grant "${store.name}" the ability to configure staff/services and accept appointment bookings?`
        }
        confirmLabel={store.appointments_enabled ? "Revoke" : "Grant"}
        destructive={store.appointments_enabled}
        isPending={toggleMutation.isPending}
        onConfirm={() => toggleMutation.mutate(!store.appointments_enabled)}
      />
    </>
  );
}
